import type { Agent } from '@studio/models/agent';
import { State } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { SnapshotSource } from '#~/api/storage/index.js';
import { getData, setData } from '#~/api/storage/index.js';
import { toIdFromHex } from '#~/api/index.js';
import type { Snapshot } from './types.js';

interface Cache {
  snapshot?: Snapshot;
  load?: Promise<Snapshot>;
  revision: number;
}
// A proxy lifetime scopes cached intent to its account and room. Legacy callers
// without an owner retain the per-agent API; all production callers supply one.
const caches = new Map<object | Agent['id'], Cache>();
const writes = new Map<Agent['id'], Promise<void>>();
const cacheFor = (key: object | Agent['id']): Cache => {
  let cache = caches.get(key);
  if (!cache) {
    cache = { revision: 0 };
    caches.set(key, cache);
  }
  return cache;
};
export const agentSnapshotRevision = (id: Agent['id'], owner?: object): number =>
  caches.get(owner ?? id)?.revision ?? 0;

/**
 * Test probes only: seed one owner's saved state without storage I/O. A probe's
 * fabricated agent has no readable snapshot, and restore fails closed on an
 * unreadable one (#1858), so the probe supplies the empty state a new camera has.
 */
export const seedAgentSnapshotForTests = (owner: object, snapshot: Snapshot): void => {
  cacheFor(owner).snapshot = snapshot;
};

export const loadAgentSnapshot = (
  id: Agent['id'],
  token: string,
  owner?: object,
  signal?: AbortSignal
): Promise<Snapshot> => {
  if (signal?.aborted)
    return Promise.reject(new DOMException('Snapshot owner retired', 'AbortError'));
  const key = owner ?? id;
  const cache = cacheFor(key);
  if (cache.snapshot) return Promise.resolve(cache.snapshot);
  if (cache.load) return cache.load;
  const load = getData(toIdFromHex(id)!, token).then((bytes) => {
    // A retired read must not decode or seed a replacement account/generation.
    if (signal?.aborted || caches.get(key) !== cache)
      throw new DOMException('Snapshot owner retired', 'AbortError');
    const result = cache.snapshot ?? (bytes.length ? State.decode(bytes) : {});
    if (cache.load === load) cache.snapshot = result;
    return result;
  });
  cache.load = load;
  const release = () => {
    if (cache.load === load) delete cache.load;
    signal?.removeEventListener('abort', release);
  };
  // Reset releases the old read immediately but keeps any newer saved intent.
  signal?.addEventListener('abort', release, { once: true });
  void load.then(release, release);
  return load;
};

export const saveAgentSnapshot = (
  id: Agent['id'],
  data: Uint8Array | SnapshotSource,
  token: string,
  owner?: object
): Promise<void> => {
  const key = owner ?? id;
  const cache = cacheFor(key);
  const remember = (bytes: Uint8Array): void => {
    if (caches.get(key) !== cache) return;
    cache.snapshot = State.decode(bytes);
    cache.revision++;
  };
  // Recovery during a save uses current local intent, including a deletion.
  remember(ArrayBuffer.isView(data) ? data : data.encode());
  const source = ArrayBuffer.isView(data)
    ? data
    : {
        ...data,
        encode: () => {
          const bytes = data.encode();
          if (writes.get(id) === write) remember(bytes);
          return bytes;
        }
      };
  const previous = writes.get(id) ?? Promise.resolve();
  // Per settle-every-terminal-branch: failures release the per-agent write queue.
  const write = previous
    .catch(() => undefined)
    .then(() => setData(toIdFromHex(id)!, source, token));
  writes.set(id, write);
  const release = () => {
    if (writes.get(id) === write) writes.delete(id);
  };
  void write.then(release, release);
  return write;
};

export const clearAgentSnapshot = (id: Agent['id'], owner?: object): void => {
  caches.delete(owner ?? id);
};

/** A reset re-reads storage unless this lifetime has a newer locally saved intent. */
export const forgetAgentSnapshotRead = (id: Agent['id'], owner: object): void => {
  const cache = caches.get(owner);
  if (cache && cache.revision === 0) clearAgentSnapshot(id, owner);
};
